import { ApiClientError, NetworkError } from '@recipes/api-client';
import type { PriceTagScan, Receipt } from '@recipes/contracts';
import { useLiveQuery } from 'dexie-react-hooks';
import { api } from '@/lib/api';
import { preparePhoto } from '@/lib/images/prepare-photo';
import { offlineDb, type PendingTripPhotoRow } from '@/lib/offline/db';
import { getPlatform } from '@/lib/platform';

/**
 * Photos of price tags and receipts (docs/partial/shopping-in-store-spec.md 6.4, 6.5). Reading
 * them needs the server; taking them does not: without a connection they wait on the device
 * (at most 20) and are sent later. The row id is the id of the scan or receipt, so a retry
 * never creates a second one.
 */
export const MAX_QUEUED_TRIP_PHOTOS = 20;

export class TripPhotoQueueFullError extends Error {
  constructor() {
    super('The photo queue is full');
    this.name = 'TripPhotoQueueFullError';
  }
}

type Photo = PendingTripPhotoRow['photos'][number];

async function upload(photo: Photo, purpose: 'AI_INPUT' | 'RECEIPT'): Promise<string> {
  const intent = await api.media.createUploadIntent({
    fileName: photo.name,
    mimeType: photo.mimeType,
    sizeBytes: photo.blob.size,
    purpose,
  });
  try {
    await api.media.upload(intent, photo.blob);
  } catch (error) {
    throw error instanceof TypeError ? new NetworkError(error) : error;
  }
  return (await api.media.complete(intent.assetId, { altText: null })).id;
}

async function toPhotos(files: readonly Blob[], name: string): Promise<Photo[]> {
  const photos: Photo[] = [];
  for (const [index, file] of files.entries()) {
    const blob = await preparePhoto(file);
    photos.push({
      blob,
      name: `${name}-${index + 1}.jpg`,
      mimeType: blob.type || 'image/jpeg',
      assetId: null,
    });
  }
  return photos;
}

async function send(row: PendingTripPhotoRow): Promise<PriceTagScan | Receipt> {
  for (const photo of row.photos) {
    if (photo.assetId) continue;
    photo.assetId = await upload(photo, row.kind === 'receipt' ? 'RECEIPT' : 'AI_INPUT');
    // Remembered at once: a retry must not upload the photo again.
    if (await offlineDb.pendingTripPhotos.get(row.id)) await offlineDb.pendingTripPhotos.put(row);
  }
  const assetIds = row.photos.map((photo) => photo.assetId!);
  if (row.kind === 'receipt')
    return api.receipts.create(
      { id: row.id, assetIds, shoppingListId: row.shoppingListId, storeId: row.storeId },
      row.id,
    );
  return api.prices.scanTag({
    id: row.id,
    assetId: assetIds[0]!,
    productId: row.productId,
    storeId: row.storeId,
  });
}

const isOffline = (error: unknown): boolean =>
  error instanceof NetworkError || (error instanceof ApiClientError && error.status === 401);

async function queue(row: PendingTripPhotoRow): Promise<void> {
  if ((await offlineDb.pendingTripPhotos.count()) >= MAX_QUEUED_TRIP_PHOTOS)
    throw new TripPhotoQueueFullError();
  await offlineDb.pendingTripPhotos.put(row);
}

export type TripPhotoResult<T> = { queued: false; result: T } | { queued: true; result: null };

async function start<T extends PriceTagScan | Receipt>(
  row: PendingTripPhotoRow,
): Promise<TripPhotoResult<T>> {
  const platform = await getPlatform();
  if (!platform.isOnline()) {
    await queue(row);
    return { queued: true, result: null };
  }
  try {
    return { queued: false, result: (await send(row)) as T };
  } catch (error) {
    if (!isOffline(error)) throw error;
    await queue(row);
    return { queued: true, result: null };
  }
}

export async function startPriceTagScan(input: {
  file: Blob;
  productId: string | null;
  storeId: string | null;
}): Promise<TripPhotoResult<PriceTagScan>> {
  return start<PriceTagScan>({
    id: crypto.randomUUID(),
    kind: 'price-tag',
    photos: await toPhotos([input.file], 'price-tag'),
    productId: input.productId,
    storeId: input.storeId,
    shoppingListId: null,
    createdAt: Date.now(),
    attempts: 0,
  });
}

export async function startReceiptScan(input: {
  files: readonly Blob[];
  shoppingListId: string | null;
  storeId: string | null;
}): Promise<TripPhotoResult<Receipt>> {
  return start<Receipt>({
    id: crypto.randomUUID(),
    kind: 'receipt',
    photos: await toPhotos(input.files, 'receipt'),
    productId: null,
    storeId: input.storeId,
    shoppingListId: input.shoppingListId,
    createdAt: Date.now(),
    attempts: 0,
  });
}

let running: Promise<number> | null = null;

/** Sends what waited for a connection. Returns how many scans and receipts were started. */
export function replayTripPhotos(): Promise<number> {
  if (running) return running;
  running = (async () => {
    let sent = 0;
    const rows = await offlineDb.pendingTripPhotos.orderBy('createdAt').toArray();
    for (const row of rows) {
      try {
        await send(row);
        await offlineDb.pendingTripPhotos.delete(row.id);
        sent += 1;
      } catch (error) {
        if (isOffline(error)) break;
        // Refused for good (AI locked, limit reached, …): it stays visible with its error.
        await offlineDb.pendingTripPhotos.update(row.id, {
          attempts: row.attempts + 1,
          lastError: error instanceof Error ? error.message : String(error),
        });
      }
    }
    return sent;
  })().finally(() => {
    running = null;
  });
  return running;
}

export async function discardTripPhoto(id: string): Promise<void> {
  await offlineDb.pendingTripPhotos.delete(id);
}

export function usePendingTripPhotos(kind?: PendingTripPhotoRow['kind']): PendingTripPhotoRow[] {
  return (
    useLiveQuery(
      () =>
        kind
          ? offlineDb.pendingTripPhotos.where('kind').equals(kind).sortBy('createdAt')
          : offlineDb.pendingTripPhotos.orderBy('createdAt').toArray(),
      [kind],
    ) ?? []
  );
}
